import type { JSX } from 'react';
import type { ReminderResource, ReminderTimingValue, ReminderTypeValue } from '@voxler/contracts';
import { reminderTypeSchema } from '@voxler/contracts';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { ReminderDialog, type ReminderDialogMode } from '../components/crm/ReminderDialog';
import { useCrmRefresh, useTodo } from '../components/crm/shared';
import { PageChrome } from '../components/page-chrome';
import {
  asOpenStage,
  crmSend,
  daysLabel,
  errorMessage,
  formatReminderDue,
  formatWeekdayDate,
  parisToday,
  REMINDER_TYPE_LABEL,
  REMINDER_TYPES,
  STAGE_LABEL,
  STAGE_NEXT_SUBJECT,
  stageBadgeVariant,
} from '../lib/crm';
import { formatEurCents } from '../lib/format';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar, CardHead } from '@/components/ui/card';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { NativeCheckbox } from '@/components/ui/field';
import { NativeSelect } from '@/components/ui/native-select';
import { PageHead } from '@/components/ui/page-header';
import {
  Cell2,
  rowLinkClass,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';
import { cn } from '@/lib/utils';

type TodoTab = 'open' | 'done';
type TypeFilter = ReminderTypeValue | 'all';

const GROUPS: readonly { timing: ReminderTimingValue; label: string }[] = [
  { timing: 'late', label: 'En retard' },
  { timing: 'today', label: 'Aujourd’hui' },
  { timing: 'upcoming', label: 'À venir' },
];

const TYPE_FILTER_LABEL: Record<ReminderTypeValue, string> = {
  call: 'Appels',
  email: 'Emails',
  meeting: 'Rendez-vous',
  task: 'Tâches',
};

function ReminderRow({
  reminder,
  busy,
  onToggle,
  onPostpone,
}: {
  reminder: ReminderResource;
  busy: boolean;
  onToggle: () => void;
  onPostpone: () => void;
}): JSX.Element {
  const done = reminder.doneAt !== null;
  return (
    <div
      className={cn(
        'grid min-h-11 grid-cols-[24px_minmax(0,1fr)] items-center gap-3 border-b border-border px-4 py-2 text-[13px] leading-[18px]',
        'min-[760px]:grid-cols-[24px_minmax(0,1fr)_auto_auto_auto]',
        '[&>*:nth-child(n+3)]:col-start-2 [&>*:nth-child(n+3)]:justify-self-start min-[760px]:[&>*:nth-child(n+3)]:col-start-auto min-[760px]:[&>*:nth-child(n+3)]:justify-self-auto',
      )}
    >
      <NativeCheckbox
        aria-label={`${done ? 'Rouvrir' : 'Marquer comme fait'} : ${reminder.subject}`}
        checked={done}
        disabled={busy}
        onChange={onToggle}
      />
      <span className="grid min-w-0 leading-[18px]">
        <span className={cn(done && 'text-muted-foreground line-through')}>{reminder.subject}</span>
        <small className="text-xs leading-4 text-muted-foreground">
          <Link
            to="/companies/$companyId"
            params={{ companyId: reminder.companyId }}
            className="text-inherit underline underline-offset-2"
          >
            {reminder.companyName}
          </Link>
          {', '}
          {reminder.dealId !== null ? (
            <Link
              to="/deals/$dealId"
              params={{ dealId: reminder.dealId }}
              className="text-inherit underline underline-offset-2"
            >
              {reminder.dealTitle}
            </Link>
          ) : (
            'sans affaire'
          )}
          {reminder.contactName !== null ? `, ${reminder.contactName}` : ''}
        </small>
      </span>
      <Tag>{REMINDER_TYPE_LABEL[reminder.type]}</Tag>
      <span className="min-w-24 whitespace-nowrap text-right font-mono text-xs text-muted-foreground">
        {formatReminderDue(reminder)}
      </span>
      <span className="flex items-center gap-1">
        {reminder.timing === 'late' ? <Badge variant="warning">En retard</Badge> : null}
        {done ? null : (
          <Button type="button" variant="ghost" size="xs" onClick={onPostpone}>
            Reporter
          </Button>
        )}
      </span>
    </div>
  );
}

export function TodoPage(): JSX.Element {
  const navigate = useNavigate();
  const refresh = useCrmRefresh();
  const todo = useTodo();
  const [tab, setTab] = useState<TodoTab>('open');
  const [type, setType] = useState<TypeFilter>('all');
  const [reminder, setReminder] = useState<ReminderDialogMode | null>(null);

  const toggle = useMutation({
    mutationFn: (item: ReminderResource) =>
      crmSend<ReminderResource>(
        'POST',
        `/reminders/${item.id}/${item.doneAt === null ? 'done' : 'reopen'}`,
      ),
    onSuccess: () => {
      refresh();
    },
  });

  const data = todo.data;
  const byType = (item: ReminderResource): boolean => type === 'all' || item.type === type;
  const open = (data?.open ?? []).filter(byType);
  const done = (data?.done ?? []).filter(byType);
  const groups =
    tab === 'done'
      ? [{ key: 'done', label: 'Terminés', items: done }]
      : GROUPS.map((group) => ({
          key: group.timing,
          label: group.label,
          items: open.filter((item) => item.timing === group.timing),
        }));
  const visible = groups.filter((group) => group.items.length > 0);
  const idle = data?.dealsWithoutNextAction ?? [];

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'À faire' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setReminder({ kind: 'create', companyId: null });
            }}
          >
            <Bell aria-hidden="true" />
            Programmer un rappel
          </Button>
        }
      />
      <PageHead
        title="À faire"
        subtitle={`Rappels et prochaines actions, du plus urgent au plus lointain. ${formatWeekdayDate(parisToday())}.`}
      />

      {todo.isError ? (
        <Callout variant="destructive">Impossible de charger les rappels.</Callout>
      ) : null}
      {toggle.isError ? (
        <Callout variant="destructive">
          {errorMessage(toggle.error, 'Échec de la mise à jour du rappel.')}
        </Callout>
      ) : null}

      {idle.length > 0 ? (
        <Card>
          <CardHead
            title="Affaires sans prochaine action"
            description="Toute affaire ouverte doit avoir un rappel programmé"
            actions={
              <Badge variant="warning">
                {idle.length} affaire{idle.length > 1 ? 's' : ''}
              </Badge>
            }
            className="pb-3"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead kind="name">Affaire</TableHead>
                <TableHead>Étape</TableHead>
                <TableHead kind="num">Pondéré</TableHead>
                <TableHead>Sans action depuis</TableHead>
                <TableHead kind="act">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {idle.map((deal) => {
                const stage = asOpenStage(deal.stage);
                return (
                  <TableRow
                    key={deal.id}
                    onClick={() =>
                      void navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })
                    }
                  >
                    <TableCell kind="name">
                      <Cell2
                        primary={
                          <Link
                            to="/deals/$dealId"
                            params={{ dealId: deal.id }}
                            className={rowLinkClass}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {deal.title}
                          </Link>
                        }
                        secondary={deal.companyName}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant={stageBadgeVariant(deal.stage)}>
                        {STAGE_LABEL[deal.stage]}
                      </Badge>
                    </TableCell>
                    <TableCell kind="num">
                      {deal.weighted !== null ? formatEurCents(deal.weighted) : 'À chiffrer'}
                    </TableCell>
                    <TableCell kind="mono">{daysLabel(deal.idleDays)}</TableCell>
                    <TableCell
                      kind="act"
                      className="w-auto"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => {
                          setReminder({
                            kind: 'create',
                            companyId: deal.companyId,
                            dealId: deal.id,
                            ...(stage !== null ? { subject: STAGE_NEXT_SUBJECT[stage] } : {}),
                          });
                        }}
                      >
                        Programmer
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      <Card>
        <CardBar
          start={
            <TableTabs<TodoTab>
              aria-label="Filtrer les rappels"
              value={tab}
              onValueChange={setTab}
              items={[
                {
                  value: 'open',
                  label: 'À faire',
                  count: data === undefined ? undefined : open.length,
                },
                {
                  value: 'done',
                  label: 'Terminés',
                  count: data === undefined ? undefined : done.length,
                },
              ]}
            />
          }
          end={
            <NativeSelect
              controlSize="sm"
              wrapperClassName="w-40"
              aria-label="Type"
              value={type}
              onChange={(event) => {
                const parsed = reminderTypeSchema.safeParse(event.target.value);
                setType(parsed.success ? parsed.data : 'all');
              }}
            >
              <option value="all">Tous les types</option>
              {REMINDER_TYPES.map((value) => (
                <option key={value} value={value}>
                  {TYPE_FILTER_LABEL[value]}
                </option>
              ))}
            </NativeSelect>
          }
        />
        {todo.isLoading ? <LoadingState /> : null}
        {visible.map((group) => (
          <div key={group.key} role="group" aria-label={group.label}>
            <div className="flex justify-between border-b border-border bg-muted px-4 py-2 text-xs font-semibold leading-4 text-muted-foreground">
              {group.label} <span className="font-mono font-medium">{group.items.length}</span>
            </div>
            {group.items.map((item) => (
              <ReminderRow
                key={item.id}
                reminder={item}
                busy={toggle.isPending}
                onToggle={() => {
                  toggle.mutate(item);
                }}
                onPostpone={() => {
                  setReminder({ kind: 'edit', reminder: item });
                }}
              />
            ))}
          </div>
        ))}
        {data !== undefined && visible.length === 0 ? (
          <EmptyState
            title={tab === 'done' ? 'Aucun rappel terminé' : 'Rien à faire'}
            description={
              tab === 'done'
                ? 'Les rappels cochés apparaissent ici.'
                : 'Les rappels cochés apparaissent dans Terminés.'
            }
          />
        ) : null}
      </Card>

      <ReminderDialog
        mode={reminder}
        onClose={() => {
          setReminder(null);
        }}
      />
    </>
  );
}
